import React from 'react';
import { common } from '@kurtosys/ksys-app-template';
import { IComponentStyle } from '@kurtosys/types/componentStyle/IComponentStyle.js';
import { computed, makeObservable } from 'mobx';
import { observer } from 'mobx-react';

import { CustomChartBase, InjectedStyledComponent } from '../../../shared/index.js';
import Legend from '../../base/Legend/Legend.js';
import { ILegendItemProps } from '../../base/Legend/models/index.js';

import {
	ISteppedBlockChartConfiguration,
	ISteppedBlockChartPoint,
	ISteppedBlockChartProps,
	ISteppedBlockChartState,
	ISteppedBlockChartStep,
} from './models/index.js';
import { ISteppedBlockChartBounds } from './models/ISteppedBlockChartBounds.js';
import Point from './styledComponents/Point.js';
import Step from './styledComponents/Step.js';
import StepBlock from './styledComponents/StepBlock.js';
import StepBlockWrapper from './styledComponents/StepBlockWrapper.js';
import StepLabel from './styledComponents/StepLabel.js';
import Steps from './styledComponents/Steps.js';
import Wrapper from './styledComponents/Wrapper.js';
import { STEPPED_BLOCK_CHART_CATEGORY_KEY, STEPPED_BLOCK_CHART_COMPONENT_KEY } from './SteppedBlockChart.meta.js';

export class SteppedBlockChart extends CustomChartBase<
	ISteppedBlockChartProps,
	ISteppedBlockChartState,
	ISteppedBlockChartConfiguration
> {
	categoryKey = STEPPED_BLOCK_CHART_CATEGORY_KEY;
	componentKey = STEPPED_BLOCK_CHART_COMPONENT_KEY;

	constructor(props: ISteppedBlockChartProps) {
		super(props);

		makeObservable(this, {
			steps: computed,
			points: computed,
		});
	}

	getBounds = (steps: ISteppedBlockChartStep[]): ISteppedBlockChartBounds => {
		const bounds: ISteppedBlockChartBounds = steps.reduce((acc: { max?: number; min?: number }, step) => {
			const { max, min } = step;
			const values = [min, max];
			for (const value of values) {
				if (common.commonUtils.isUndefined(acc.max)) {
					acc.max = value;
				} else {
					acc.max = Math.max(value, acc.max);
				}
				if (common.commonUtils.isUndefined(acc.min)) {
					acc.min = value;
				} else {
					acc.min = Math.min(value, acc.min);
				}
			}

			return acc;
		}, {});
		bounds.count = steps.length;

		// The benchmark will always be at 0 so we must have it on the range
		if (typeof bounds.max === 'number' && typeof bounds.min === 'number') {
			bounds.min = Math.min(bounds.min, 0);
			bounds.max = Math.max(bounds.max, 0);
			bounds.range = bounds.max - bounds.min;
		}
		return bounds;
	};

	scale(value: number, bounds: ISteppedBlockChartBounds): number {
		const offsetValue = value - (bounds.min || 0);
		return (offsetValue / (bounds.range || 1)) * 100;
	}

	get steps(): ISteppedBlockChartStep[] {
		const { steps: rawSteps = [] } = this.props;
		const sortedSteps = rawSteps.sort((aStep, bStep) => {
			return aStep.max - bStep.max;
		});
		return sortedSteps;
	}

	get points(): ISteppedBlockChartPoint[] {
		const { points = [] } = this.props;
		return points;
	}

	getPointsForStep = (stepIndex: number, steps: ISteppedBlockChartStep[]) => {
		const step = steps[stepIndex];
		const { lastStepCatch = false } = this.props;
		const points = this.points;
		const isLastStep = stepIndex === steps.length - 1;
		const { min, max } = step;
		return points.filter(
			(point) => (point.value >= min && point.value < max) || (lastStepCatch && isLastStep && point.value >= max),
		);
	};

	getLegendItems() {
		return this.points.map((point) => {
			const legend: ILegendItemProps = {
				label: {
					value: point.label,
				},
				color: {
					color: point.color,
				},
			};

			if (point.value !== undefined && !isNaN(point.value)) {
				legend.value = {
					value: typeof point.value === 'number' ? point.value.toString() : point.value,
				};
			}

			return legend;
		});
	}

	render() {
		const { className, applyStepColorToLabelText } = this.props;
		const steps = this.steps;
		const legendProps = this.legendProps;
		const legendPosition = this.legendPosition;
		const bounds = this.getBounds(steps);
		return (
			<Wrapper className={className}>
				{legendProps && legendPosition === 'top' && <Legend {...legendProps} />}
				<Steps>
					{steps.map((step, stepIndex) => {
						const { min, max, label = `${min}-${max}`, color: stepColor } = step;
						const stepKey = `${min}_${max}`;
						const stepBlockComponentStyle: IComponentStyle = {
							height: `${this.scale(max, bounds)}%`,
						};
						if (stepColor) {
							stepBlockComponentStyle.background = {
								color: stepColor,
							};
						}
						let stepLabelComponentStyle: IComponentStyle | undefined;
						if (stepColor && applyStepColorToLabelText) {
							stepLabelComponentStyle = {
								color: stepColor,
							};
						}
						const points = this.getPointsForStep(stepIndex, steps);
						const stepBounds = this.getBounds([step]);
						return (
							<Step key={stepKey}>
								<StepBlockWrapper>
									<StepBlock componentStyle={stepBlockComponentStyle}>
										{points.map((point, pointIndex) => {
											const { value: rawValue, color: pointColor } = point;
											const pointKey = `${rawValue}_${pointColor}_${pointIndex}`;
											const value = Math.min(max, rawValue);
											const pointsCount = points.length;
											const pointsLeftInterval = 100 / pointsCount;
											const pointsLeftSubInterval = pointsLeftInterval / 2;
											const pointLeft = pointsLeftInterval * pointIndex + pointsLeftSubInterval;
											const pointComponentStyle: IComponentStyle = {
												top: `${100 - this.scale(value, stepBounds)}%`,
												left: `${pointLeft}%`,
												zIndex: `${pointsCount - pointIndex}`,
											};
											if (pointColor) {
												pointComponentStyle.background = {
													color: pointColor,
												};
											}
											return <Point key={pointKey} componentStyle={pointComponentStyle} />;
										})}
									</StepBlock>
								</StepBlockWrapper>
								<StepLabel componentStyle={stepLabelComponentStyle}>{label}</StepLabel>
							</Step>
						);
					})}
				</Steps>
				{legendProps && legendPosition === 'bottom' && <Legend {...legendProps} />}
			</Wrapper>
		);
	}
}

export default InjectedStyledComponent(
	observer(SteppedBlockChart),
	STEPPED_BLOCK_CHART_CATEGORY_KEY,
	STEPPED_BLOCK_CHART_COMPONENT_KEY,
);
